import React from "react";
import { Btn, Header, BodyWithClass } from "@client/ui";
import { useNavigate } from "react-router-dom";
import { HEIGHTS } from "../../utils/constants";

const DriverInformation = () => {
  const navigate = useNavigate();

  const navigateToSignup = () => {
    navigate("/driver/signup");
  };

  return (
    <div
      className="body-width"
      style={{ margin: "auto", maxWidth: 1000, paddingBottom: 50 }}
    >
      {/* Hero section */}
      <div style={{ textAlign: "center", marginBottom: 40, marginTop: 30 }}>
        <Header text="Drive with Tapzus" />
        <p style={{ fontSize: 18, color: "#666", marginTop: 10 }}>
          Flexible hours, competitive earnings, and the freedom to be your own
          boss
        </p>
        <div style={{ marginTop: 30 }}>
          <Btn
            text="Sign Up to Drive"
            onClick={navigateToSignup}
            style={{
              width: 200,
              backgroundColor: "#2563eb",
              padding: "10px 20px",
              fontSize: 16,
            }}
          />
        </div>
      </div>

      {/* How it works section */}
      <section style={{ marginBottom: 60 }}>
        <BodyWithClass
          text="How it works"
          className="poppins-semibold"
          style={{ fontSize: 24, marginBottom: 20 }}
        />

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 20,
          }}
        >
          {[
            {
              title: "Sign up",
              description:
                "Create your account with basic information to get started",
              icon: "📝",
            },
            {
              title: "Complete verification",
              description:
                "Upload required documents to verify your identity and vehicle information",
              icon: "✅",
            },
            {
              title: "Get approved",
              description:
                "Once verified, you'll receive approval to start driving",
              icon: "👍",
            },
            {
              title: "Start earning",
              description:
                "Accept rides through the Tapzus app and start making money",
              icon: "💰",
            },
          ].map((step, index) => (
            <div
              key={index}
              style={{
                flex: "1 1 45%",
                minWidth: 280,
                padding: 20,
                backgroundColor: "#f8fafc",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 10 }}>{step.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
                {step.title}
              </h3>
              <p style={{ color: "#4b5563" }}>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits section */}
      <section style={{ marginBottom: 60 }}>
        <BodyWithClass
          text="Why drive with Tapzus?"
          className="poppins-semibold"
          style={{ fontSize: 24, marginBottom: 20 }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {[
            {
              title: "Flexible schedule",
              description:
                "Drive whenever you want - mornings, evenings, or weekends. You're in control of your schedule.",
            },
            {
              title: "Competitive earnings",
              description:
                "Earn competitive rates with each trip you complete, plus keep 100% of your tips.",
            },
            {
              title: "Quick payments",
              description:
                "Get paid weekly with direct deposits to your bank account.",
            },
            {
              title: "24/7 support",
              description:
                "Access support whenever you need it through the app.",
            },
            {
              title: "Safety features",
              description:
                "Built-in safety features to protect you during every trip.",
            },
          ].map((benefit, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 15,
                padding: "15px 0",
                borderBottom: index !== 4 ? "1px solid #e5e7eb" : "none",
              }}
            >
              <div
                style={{
                  backgroundColor: "#dbeafe",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "#2563eb",
                  fontWeight: "bold",
                }}
              >
                {index + 1}
              </div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 5 }}>
                  {benefit.title}
                </h3>
                <p style={{ color: "#4b5563", lineHeight: 1.5 }}>
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Requirements section */}
      <section style={{ marginBottom: 60 }}>
        <BodyWithClass
          text="Requirements"
          className="poppins-semibold"
          style={{ fontSize: 24, marginBottom: 20 }}
        />

        <div
          style={{
            backgroundColor: "#f8fafc",
            padding: 25,
            borderRadius: 8,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
            {[
              "Valid driver's license",
              "Proof of vehicle insurance",
              "Vehicle registration",
              "At least 21 years old",
              "At least 1 year of driving experience",
              "Smartphone compatible with the Tapzus Driver app",
              "Vehicle in good condition (2010 or newer)",
              "Pass a background check",
            ].map((requirement, index) => (
              <li
                key={index}
                style={{
                  padding: "10px 0",
                  display: "flex",
                  alignItems: "center",
                  borderBottom: index !== 7 ? "1px solid #e5e7eb" : "none",
                }}
              >
                <span
                  style={{
                    color: "#2563eb",
                    marginRight: 10,
                    fontSize: 18,
                  }}
                >
                  ✓
                </span>
                {requirement}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ section */}
      <section style={{ marginBottom: 60 }}>
        <BodyWithClass
          text="Frequently Asked Questions"
          className="poppins-semibold"
          style={{ fontSize: 24, marginBottom: 20 }}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
          {[
            {
              question: "How much can I earn as a Tapzus driver?",
              answer:
                "Earnings vary based on your location, the number of hours you drive, and the demand in your area. Many drivers report earnings between ₦25,000 - ₦60,000 daily when driving full-time.",
            },
            {
              question: "Do I need to have a commercial driver's license?",
              answer:
                "No, a standard driver's license is sufficient to drive with Tapzus, but it must be valid and in good standing.",
            },
            {
              question: "How does the payment process work?",
              answer:
                "Drivers receive weekly payments directly to their bank accounts. The money you earn from Monday to Sunday will be transferred to your account by Tuesday of the following week.",
            },
            {
              question:
                "Can I drive for other rideshare services while driving for Tapzus?",
              answer:
                "Yes, you're free to drive for other services in addition to Tapzus.",
            },
            {
              question: "What expenses will I be responsible for as a driver?",
              answer:
                "As a driver, you're responsible for your vehicle's expenses including fuel, maintenance, insurance, and any applicable taxes.",
            },
          ].map((faq, index) => (
            <div
              key={index}
              style={{
                padding: 20,
                backgroundColor: "#fff",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 10,
                  color: "#2563eb",
                }}
              >
                {faq.question}
              </h3>
              <p style={{ color: "#4b5563", lineHeight: 1.5 }}>{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        style={{
          textAlign: "center",
          marginBottom: 60,
          backgroundColor: "#2563eb",
          padding: 40,
          borderRadius: 8,
          color: "white",
        }}
      >
        <h2 style={{ fontSize: 28, fontWeight: 700, marginBottom: 15 }}>
          Ready to hit the road?
        </h2>
        <p
          style={{
            fontSize: 18,
            marginBottom: 30,
            maxWidth: 600,
            margin: "0 auto 30px",
          }}
        >
          Join thousands of drivers earning on their own schedule with Tapzus.
        </p>
        <Btn
          text="Sign Up Now"
          onClick={navigateToSignup}
          style={{
            backgroundColor: "white",
            color: "#2563eb",
            padding: "12px 30px",
            fontSize: 18,
            fontWeight: 600,
          }}
        />
      </section>
    </div>
  );
};

export default DriverInformation;
